'use client';

/**
 * Vendor Collections Widget
 *
 * @component_role Vendor Product Catalog Browser
 * @business_rule Displays vendor's product collections with thumbnails from GCS.
 *   Click collection to drill into products; click product to show in Copilot panel.
 *   Uses tab store to persist selected collection per vendor tab.
 * @user_outcome Browse vendor catalogs visually like a product website,
 *   not a text list. See images, prices, and availability at a glance.
 * @integration_point klyde_edge /neo/graph/explore/vendor - retrieves collections/products from Neo4j
 * @integration_point GCS klyde-documents bucket - product images via signed URLs
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain vendor_catalog
 */

import { Loader2, Grid2X2, Box, ChevronRight, Package, ShoppingCart, Check } from 'lucide-react';
import { useState, useCallback } from 'react';
import { useVendorExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCartStore } from '@/lib/stores/cart-store';
import { cn } from '@/lib/utils/cn';
import type { CollectionDetail, ProductSummary } from '@/lib/api/types';
import { testIds } from '@/lib/test-utils/selectors';

interface VendorCollectionsWidgetProps {
  /** Standard prop from registry */
  entityId?: string;
  /** Standard prop from registry */
  title?: string;
  /** @deprecated Use entityId */
  vendorId?: string;
  /** @deprecated Use title */
  vendorName?: string;
  onProductClick?: (product: ProductSummary, collectionName: string) => void;
}

export function VendorCollectionsWidget({
  entityId,
  title,
  vendorId: legacyVendorId,
  vendorName: legacyVendorName,
  onProductClick,
}: VendorCollectionsWidgetProps) {
  // Support both standard and legacy props
  const vendorId = entityId ?? legacyVendorId ?? '';
  const vendorName = title ?? legacyVendorName ?? '';
  const vendorExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_explore'));
  const { data, isLoading, error } = useVendorExplore(vendorExploreStrategy!, { vendor_id: vendorId });

  // Connect to graph selection store for copilot panel
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Handle product click - show details in copilot panel
  const handleProductClick = (product: ProductSummary, collectionName: string) => {
    selectNode({
      id: product.semantic_id,
      label: product.name,
      type: 'product',
      properties: {
        list_price: product.list_price,
        image_url: product.image_url,
        collection: collectionName,
        vendor: vendorName,
      }
    });
    // Also call legacy callback if provided
    onProductClick?.(product, collectionName);
  };

  // Get collection selection from tab store
  const tabId = `vendor-${vendorId}`;
  const tabs = useTabStore((state) => state.tabs);
  const updateTab = useTabStore((state) => state.updateTab);

  const currentTab = tabs.find((t) => t.id === tabId);
  const selectedCollectionId = currentTab?.selectedCollectionId;

  // Find the selected collection from data
  const selectedCollection = selectedCollectionId
    ? data?.vendor?.collections.find((c) => c.semantic_id === selectedCollectionId) ||
      (selectedCollectionId === 'uncategorized' && data?.vendor?.uncategorized_products.length
        ? {
            semantic_id: 'uncategorized',
            name: 'Uncategorized',
            product_count: data.vendor.uncategorized_products.length,
            products: data.vendor.uncategorized_products,
          }
        : null)
    : null;

  const setSelectedCollection = (collection: CollectionDetail | null) => {
    updateTab(tabId, { selectedCollectionId: collection?.semantic_id || null });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data?.vendor) {
    return (
      <div className="flex items-center justify-center h-full text-destructive">
        Failed to load vendor data
      </div>
    );
  }

  const { vendor } = data;

  // Show collection detail view
  if (selectedCollection) {
    return (
      <div data-testid={testIds.vendorCatalog.container} className="h-full flex flex-col">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-panel-border bg-panel">
          <button
            data-testid={testIds.vendorCatalog.backButton}
            onClick={() => setSelectedCollection(null)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {vendorName}
          </button>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">{selectedCollection.name}</span>
          <span className="text-xs text-muted-foreground ml-auto">
            {selectedCollection.product_count} products
          </span>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-auto p-4">
          <div data-testid={testIds.vendorCatalog.productGrid} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {selectedCollection.products.map((product) => (
              <ProductCard
                key={product.semantic_id}
                product={product}
                vendorId={vendorId}
                vendorName={vendorName}
                onClick={() => handleProductClick(product, selectedCollection.name)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show collections overview
  return (
    <div data-testid={testIds.vendorCatalog.container} className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-panel-border bg-panel">
        <div>
          <h2 className="text-lg font-semibold">{vendorName}</h2>
          <p className="text-sm text-muted-foreground">
            {vendor.collections.length} collections • {data.metadata.product_count} products
          </p>
        </div>
      </div>

      {/* Collections Grid */}
      <div className="flex-1 overflow-auto p-4">
        <div data-testid={testIds.vendorCatalog.collectionGrid} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {vendor.collections.map((collection) => (
            <CollectionCard
              key={collection.semantic_id}
              collection={collection}
              onClick={() => setSelectedCollection(collection)}
            />
          ))}

          {/* Uncategorized products as pseudo-collection */}
          {vendor.uncategorized_products.length > 0 && (
            <button
              onClick={() =>
                setSelectedCollection({
                  semantic_id: 'uncategorized',
                  name: 'Uncategorized',
                  product_count: vendor.uncategorized_products.length,
                  products: vendor.uncategorized_products,
                })
              }
              className={cn(
                'group relative rounded-lg border border-panel-border overflow-hidden',
                'hover:border-primary/50 transition-colors text-left'
              )}
            >
              <div className="aspect-square bg-muted flex items-center justify-center">
                <Package className="w-12 h-12 text-muted-foreground" />
              </div>
              <div className="p-3">
                <h3 className="font-medium text-sm truncate">Uncategorized</h3>
                <p className="text-xs text-muted-foreground">
                  {vendor.uncategorized_products.length} products
                </p>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CollectionCard({
  collection,
  onClick,
}: {
  collection: CollectionDetail;
  onClick: () => void;
}) {
  // Get first product with an image as thumbnail
  const thumbnailProduct = collection.products.find((p) => p.image_url);
  const thumbnailUrl = thumbnailProduct?.image_url;

  return (
    <button
      data-testid={testIds.vendorCatalog.collectionCard(collection.semantic_id)}
      onClick={onClick}
      className={cn(
        'group relative rounded-lg border border-panel-border overflow-hidden',
        'hover:border-primary/50 transition-colors text-left'
      )}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-muted relative">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={collection.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback on image error
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Grid2X2 className="w-12 h-12 text-muted-foreground" />
          </div>
        )}

        {/* Product count badge */}
        <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-0.5 rounded text-xs font-medium">
          {collection.product_count}
        </div>
      </div>

      {/* Label */}
      <div className="p-3">
        <h3 className="font-medium text-sm truncate group-hover:text-primary transition-colors">
          {collection.name}
        </h3>
        <p className="text-xs text-muted-foreground">
          {collection.product_count} product{collection.product_count !== 1 ? 's' : ''}
        </p>
      </div>
    </button>
  );
}

function ProductCard({
  product,
  vendorId,
  vendorName,
  onClick,
}: {
  product: ProductSummary;
  vendorId: string;
  vendorName: string;
  onClick?: () => void;
}) {
  const [justAdded, setJustAdded] = useState(false);
  const addItem = useCartStore((state) => state.addItem);
  // Select actual state slice for proper Zustand reactivity
  const items = useCartStore((state) => state.items);

  // "Is this product anywhere in the cart?" — product-keyed by design.
  // Use the store's isInCart() if you don't also need to inspect lines.
  const isInCart = items.some((item) => item.product_sid === product.semantic_id);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Don't trigger card click twice
      // Also select the product so Copilot shows details
      onClick?.();
      addItem({
        product_sid: product.semantic_id,
        name: product.name,
        quantity: 1,
        vendor_id: vendorId,
        vendor_name: vendorName,
        list_price: product.list_price,
        image_url: product.image_url,
      });
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    },
    [addItem, product, vendorId, vendorName, onClick]
  );

  return (
    <div
      data-testid={testIds.vendorCatalog.productCard(product.semantic_id)}
      onClick={onClick}
      className={cn(
        'group relative rounded-lg border border-panel-border overflow-hidden cursor-pointer',
        'hover:border-primary/50 transition-colors text-left'
      )}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-muted relative">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Box className="w-8 h-8 text-muted-foreground" />
          </div>
        )}

        {/* Add to RFQ Button */}
        <button
          data-testid={testIds.vendorCatalog.addToCart(product.semantic_id)}
          onClick={handleAddToCart}
          className={cn(
            'absolute top-2 right-2 p-1.5 rounded-md transition-all',
            'opacity-0 group-hover:opacity-100',
            justAdded
              ? 'bg-green-600 text-white'
              : isInCart
                ? 'bg-primary/80 text-primary-foreground'
                : 'bg-background/80 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground'
          )}
          title={isInCart ? 'Add more to RFQ' : 'Add to RFQ'}
        >
          {justAdded ? (
            <Check className="w-4 h-4" />
          ) : (
            <ShoppingCart className="w-4 h-4" />
          )}
        </button>

        {/* In Cart Badge */}
        {isInCart && !justAdded && (
          <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">
            In Cart
          </div>
        )}
      </div>

      {/* Label */}
      <div className="p-2">
        <h3 className="font-medium text-xs truncate group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        {product.list_price && (
          <p className="text-xs text-muted-foreground">
            ${product.list_price.toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
